import $ from 'jquery';
import moment from 'moment';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {
  BENCHLING_LOCALE_TAGS,
  DEFAULT_DISPLAY_NUMBER,
  DEFAULT_LANGUAGE_TAG,
  LanguageTag,
} from 'i18n/LanguageTagConstants';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import LegacySelect2, {LegacySelect2Query, LegacySelect2Result} from 'ui-library/LegacySelect2';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import * as DateUtil from 'util/DateUtil';
import lruMemoize from 'util/lruMemoize';
import {localeAwareFormatNumber} from 'util/numbers/NumberFormattingUtil';

import {BenchlingLocale, LOCALE_ENUM_TO_NAME} from './AllLanguageTags';
import LanguageTagsToNumberParsingFormat from './languageTagsToNumberParsingFormat.json';

function stringToBenchlingLocale(locale: string): BenchlingLocale {
  return BenchlingLocale[locale.replace('-', '_').toUpperCase() as keyof typeof BenchlingLocale];
}

export function getLocaleName(code: string): string {
  return LOCALE_ENUM_TO_NAME[stringToBenchlingLocale(code)];
}

function isLanguageSupportedByBrowser(code: string): boolean {
  try {
    return Intl.DateTimeFormat.supportedLocalesOf([code], {localeMatcher: 'lookup'}).length !== 0;
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    return false;
  }
}

export function getSupportedLanguageTags(): Array<LanguageTag> {
  return Object.keys(LOCALE_ENUM_TO_NAME).flatMap((locale) =>
    (BenchlingConfig.NAV_ENABLE_CUSTOM_LOCALES && BENCHLING_LOCALE_TAGS.hasOwnProperty(locale)) ||
    isLanguageSupportedByBrowser(locale)
      ? {code: locale, name: getLocaleName(locale)}
      : []
  );
}

function getDatePreview(date: moment.MomentInput, result: LegacySelect2Result): string {
  return DateUtil.readableTime(date, {
    languageTag: result.id,
    showYear: true,
    showDate: true,
    showTime: true,
    showWeekday: true,
  });
}

function dateValueToDisplayString(displayValue: number): string {
  return displayValue.toString().padStart(2, '0');
}

export function _getShortDatePreview(date: Date, languageTag: string): string {
  // add one to getMonth() since getMonth() returns the month as a zero-based value
  const month = dateValueToDisplayString(date.getMonth() + 1);
  const day = dateValueToDisplayString(date.getDate());
  const year = date.getFullYear();
  return DateUtil.expectsMonthBeforeDay(languageTag) ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
}

const DISPLAY_SEPARATOR = ' | ';

function formatDisplayNumberWithLanguageTag(languageTag: string): string {
  languageTag =
    LanguageTagsToNumberParsingFormat[languageTag] != null ? languageTag : DEFAULT_LANGUAGE_TAG.code;
  return localeAwareFormatNumber(
    Number(DEFAULT_DISPLAY_NUMBER),
    LanguageTagsToNumberParsingFormat[languageTag],
    true
  );
}

// Locale date can have non-Latin characters ex. الجمعة، ٤/٨/١٤٤٤ هـ ١٠:٢٩ ص
function formatSelectionWithNumberAndLocaleDate(
  result: LegacySelect2Result,
  date: moment.MomentInput
): string {
  const datePreview = getDatePreview(date, result);
  const numberPreview = formatDisplayNumberWithLanguageTag(result.id);
  return [numberPreview, datePreview].join(DISPLAY_SEPARATOR);
}

function formatSelectionWithNameNumberAndLocaleDate(
  result: LegacySelect2Result,
  date: moment.MomentInput
): string {
  const dateAndNumberPreview = formatSelectionWithNumberAndLocaleDate(result, date);
  return [result.text, dateAndNumberPreview].join(DISPLAY_SEPARATOR);
}

function formatSelectionWithNameNumberAndShortDate(result: LanguageTag, date: Date): string {
  const datePreview = _getShortDatePreview(date, result.code);
  const numberPreview = formatDisplayNumberWithLanguageTag(result.code);
  return [result.name, numberPreview, datePreview].join(DISPLAY_SEPARATOR);
}

export const _memoizedFormatSelectionWithNameNumberAndShortDate = lruMemoize(
  formatSelectionWithNameNumberAndShortDate,
  {max: 200},
  (result: LanguageTag, date: Date) => `${result.code};${result.name};${date}`
);
interface UncontrolledLanguagePickerInputProps {
  name: string;
  date: moment.MomentInput;
  defaultValue: string;
}

/**
 * An uncontrolled <input> component to allow a user to select an IANA language tag,
 * showing a preview of a datetime for the selected language.
 *
 * @param props.name - the <input>'s name attribute
 * @param props.defaultValue - the initially selected language tag.
 */
export function UncontrolledLanguagePickerInput(props: UncontrolledLanguagePickerInputProps): JSX.Element {
  const options = getSupportedLanguageTags().map((tag) => ({id: tag.code, text: tag.name}));
  return (
    <div className="u-small-spacing-bottom">
      <LegacySelect2
        options={options}
        name={props.name}
        placeholder="Select language"
        initSelection={(element, callback) => {
          if (!props.defaultValue) {
            // If there is no initial value, fallback to the placeholder.
            return;
          }
          callback(
            options.find((option) => option.id === props.defaultValue) || {
              id: props.defaultValue,
              text: `Unknown (${_.escape(props.defaultValue)})`,
            }
          );
        }}
        defaultValue={props.defaultValue}
        formatSelection={(result) =>
          BenchlingConfig.ELN_ENABLE_LOCALE_AWARE_PARSING_FOR_COPY_PASTE
            ? formatSelectionWithNameNumberAndLocaleDate(result, props.date)
            : `${result.text} / ${getDatePreview(props.date, result)}`
        }
        formatResult={function _formatResult(
          result: LegacySelect2Result,
          container: JQuery,
          query: LegacySelect2Query,
          escapeMarkup: (markup: string) => string
        ): string {
          const resultHTML = $.fn.select2.defaults.formatResult!(result, container, query, escapeMarkup);
          return ReactDOMServer.renderToStaticMarkup(
            <div>
              <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{__html: resultHTML}}
              />
              <div className="faded">
                {BenchlingConfig.ELN_ENABLE_LOCALE_AWARE_PARSING_FOR_COPY_PASTE
                  ? formatSelectionWithNumberAndLocaleDate(result, props.date)
                  : getDatePreview(props.date, result)}
              </div>
            </div>
          );
        }}
      />
    </div>
  );
}

interface ControlledLanguagePickerInputProps {
  languageTag: string;
  date: Date;
  onSelect: (languageTag: string) => void;
}

function getLanguageTagFromLanguageTagCode(options: {[code: string]: string}, code: string): LanguageTag {
  return options[code] != null ? {code, name: options[code]} : DEFAULT_LANGUAGE_TAG;
}

export function ControlledLanguagePickerInput(props: ControlledLanguagePickerInputProps): JSX.Element {
  const options: {[code: string]: string} = Object.fromEntries(
    getSupportedLanguageTags().map((tag) => [tag.code, tag.name])
  );
  return (
    <div className="u-small-spacing-bottom">
      <SelectDropdown
        displayName={_memoizedFormatSelectionWithNameNumberAndShortDate(
          getLanguageTagFromLanguageTagCode(options, props.languageTag),
          props.date
        )}
        allowMenuToCloseDropdown={false}
        menu={
          <SearchableMenu
            selectedValue={props.languageTag}
            onSelect={props.onSelect}
            optionDatas={Object.entries(options).map(([code, name]) => ({
              name: _memoizedFormatSelectionWithNameNumberAndShortDate({code, name}, props.date),
              value: code,
            }))}
            height={Menu.HEIGHTS.MEDIUM}
            width={Menu.WIDTHS.FULL}
          />
        }
        dataTestAttrs={{component: 'ControlledLanguagePickerInput', element: 'root'}}
      />
    </div>
  );
}
